/**
 * Input Suggestions Component
 * Smart autocomplete and quick suggestions for common queries
 */

'use client';

import { useEffect, useState } from 'react';
import { ArrowRight, Lightbulb } from 'lucide-react';

interface Suggestion {
  text: string;
  category: 'question' | 'feature' | 'brand' | 'policy';
}

const COMMON_SUGGESTIONS: Suggestion[] = [
  { text: 'What mattress is best for back pain?', category: 'question' },
  { text: 'Show me cooling mattresses', category: 'feature' },
  { text: 'Tell me about your return policy', category: 'policy' },
  { text: 'What sizes do you have?', category: 'question' },
  { text: 'Do you offer free shipping?', category: 'policy' },
  { text: 'What is the firmness scale?', category: 'question' },
  { text: 'Show me memory foam options', category: 'feature' },
  { text: 'What is your warranty?', category: 'policy' },
  { text: 'Best mattress for side sleepers', category: 'question' },
  { text: 'Do you have hybrid mattresses?', category: 'feature' },
];

export function InputSuggestions({
  input,
  onSelect,
  isVisible,
}: {
  input: string;
  onSelect: (text: string) => void;
  isVisible: boolean;
}) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  useEffect(() => {
    if (!input.trim() || input.length < 2) {
      setSuggestions([]);
      return;
    }

    const lowerInput = input.toLowerCase();
    const filtered = COMMON_SUGGESTIONS.filter((s) =>
      s.text.toLowerCase().includes(lowerInput)
    ).slice(0, 3);

    setSuggestions(filtered);
  }, [input]);

  if (!isVisible || suggestions.length === 0) return null;

  return (
    <div className="absolute bottom-full left-0 right-0 mb-2 bg-card/95 backdrop-blur-sm border border-foreground/10 rounded-xl shadow-lg overflow-hidden animate-message-slide-up z-10">
      <div className="p-2 space-y-1">
        {suggestions.map((suggestion, idx) => (
          <button
            key={idx}
            onClick={() => onSelect(suggestion.text)}
            className="input-suggestion-item w-full text-left px-3 py-2 rounded-lg hover:bg-muted/70 transition-colors flex items-center gap-2 group"
          >
            <Lightbulb size={14} className="text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
            <span className="text-sm text-foreground truncate">
              {suggestion.text}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function QuickStartSuggestions({
  onSelect,
}: {
  onSelect: (text: string) => void;
}) {
  const quickStarts: Suggestion[] = [
    { text: 'Help me find the perfect mattress', category: 'question' },
    { text: 'What makes your mattresses special?', category: 'question' },
    { text: 'Tell me about your best sellers', category: 'feature' },
    { text: 'I need help with back pain', category: 'question' },
  ];

  return (
    <div className="px-4 pb-4">
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb size={14} className="text-muted-foreground" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Quick starts
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {quickStarts.map((suggestion, idx) => (
          <button
            key={idx}
            onClick={() => onSelect(suggestion.text)}
            className="group relative px-5 py-3.5 text-left rounded-2xl quick-reply-button response-button transition-all duration-200 active:scale-[0.98]"
          >
            <div className="flex items-center justify-between">
              <span className="text-[15px] font-medium tracking-[-0.011em] text-[inherit]">
                {suggestion.text}
              </span>
              <ArrowRight size={16} className="text-[inherit] opacity-60 group-hover:opacity-95 group-hover:translate-x-0.5 transition-all duration-200" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
